import React from 'react';

import { PlusIcon } from '@common_components/icons/plus.icon';
import type { ChartSettingsListSectionProps } from './chartSectionList.interfaces';
import { ChevronUpIcon, ChevronDownIcon, CrossIcon, ArrowShortUpIcon } from '@umanforge/common';
import { TrashIcon } from '@common_components/icons/trash.icon';

const ChartSettingsListSection: React.FC<ChartSettingsListSectionProps> = ( {
  t: translate,
  isLoadingSettings,
  loadSettingsError,
  chartSettings,
  pendingSettings,
  pendingUpdatedSettings,
  pendingDeletedSettings,
  onAdd,
  onSelectSetting,
  onMarkDeleteSetting,
  onRestorePendingDelete,
  onRemovePending,
  onRemovePendingUpdate,
  onMoveSetting,
  onMovePendingSetting,
} ) => {
  if ( isLoadingSettings ) {
    return (
      <p className="text-xs text-gray-500">
        {translate( 'translation:chart_settings_loading' )}
      </p>
    );
  }

  if ( loadSettingsError ) {
    return (
      <p className="text-xs text-red-600">
        {loadSettingsError}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={onAdd}
        className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 w-full text-left"
      >
        <PlusIcon className="w-3 h-3" />
        {translate( 'translation:add_chart_setting' )}
      </button>

      {pendingSettings.length > 0 && (
        <>
          {pendingSettings.map( ( pending, index ) => {
            const canMoveUp = index > 0;
            const canMoveDown = index < pendingSettings.length - 1;
            return (
              <div
                key={index}
                className="flex items-center justify-between px-2 py-1.5 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded"
              >
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className={`text-orange-400 hover:text-orange-600 px-0.5 ${canMoveUp ? '' : 'invisible'}`}
                    onClick={() => { return onMovePendingSetting && onMovePendingSetting( index, index - 1 ); }}
                    disabled={!canMoveUp}
                  >
                    <ChevronUpIcon className="w-3 h-3 rotate-180" />
                  </button>
                  <button
                    type="button"
                    className={`text-orange-400 hover:text-orange-600 px-0.5 ${canMoveDown ? '' : 'invisible'}`}
                    onClick={() => { return onMovePendingSetting && onMovePendingSetting( index, index + 1 ); }}
                    disabled={!canMoveDown}
                  >
                    <ChevronDownIcon className="w-3 h-3" />
                  </button>
                </div>
                <span className="truncate flex-1 px-1">{pending.values.title}</span>
                <button
                  type="button"
                  onClick={() => { return onRemovePending( index ); }}
                  className="ml-2 text-orange-400 hover:text-orange-600 flex-shrink-0"
                >
                  <CrossIcon className="w-3 h-3" />
                </button>
              </div>
            );
          } )}
        </>
      )}

      {pendingUpdatedSettings.length > 0 && (
        <>
          {pendingUpdatedSettings.map( pending => {
            return (
              <div
                key={pending.id}
                className="flex items-center justify-between px-2 py-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded"
              >
                <span className="truncate flex-1">{pending.values.title}</span>
                <button
                  type="button"
                  onClick={() => { return onRemovePendingUpdate( pending.id ); }}
                  className="ml-2 text-blue-400 hover:text-blue-600 flex-shrink-0"
                >
                  <CrossIcon className="w-3 h-3" />
                </button>
              </div>
            );
          } )}
        </>
      )}

      {pendingDeletedSettings.length > 0 && (
        <>
          {pendingDeletedSettings.map( pending => {
            return (
              <div
                key={pending.setting.id}
                className="flex items-center justify-between px-2 py-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded"
              >
                <span className="truncate flex-1 line-through">{pending.setting.title}</span>
                <button
                  type="button"
                  onClick={() => { return onRestorePendingDelete( pending.setting.id ); }}
                  className="ml-2 text-red-400 hover:text-red-600 flex-shrink-0"
                >
                  <ArrowShortUpIcon className="w-3 h-3" />
                </button>
              </div>
            );
          } )}
        </>
      )}

      {chartSettings.map( ( setting, idx ) => {
        const canMoveUp = idx > 0;
        const canMoveDown = idx < chartSettings.length - 1;
        return (
          <div
            key={setting.id}
            className="flex items-center justify-between px-2 py-1.5 text-xs text-gray-700 border border-gray-200 rounded hover:bg-gray-50 w-full"
          >
            <div className="flex items-center gap-1">
              <button
                type="button"
                className={`text-blue-500 hover:text-blue-700 px-0.5 ${canMoveUp ? '' : 'invisible'}`}
                onClick={() => {return onMoveSetting && onMoveSetting( idx, idx - 1 );}}
                disabled={!canMoveUp}
              >
                <ChevronUpIcon className="w-3 h-3 rotate-180" />
              </button>
              <button
                type="button"
                className={`text-blue-500 hover:text-blue-700 px-0.5 ${canMoveDown ? '' : 'invisible'}`}
                onClick={() => {return onMoveSetting && onMoveSetting( idx, idx + 1 );}}
                disabled={!canMoveDown}
              >
                <ChevronDownIcon className="w-3 h-3" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => { onSelectSetting?.( setting ); }}
              className="flex-1 text-left truncate"
            >
              {setting.title}
            </button>
            <button
              type="button"
              onClick={() => { return onMarkDeleteSetting( setting ); }}
              className="ml-2 text-red-400 hover:text-red-600 flex-shrink-0"
            >
              <TrashIcon className="w-3 h-3" size={3} />
            </button>
          </div>
        );
      } )}
      {chartSettings.length === 0 && pendingSettings.length === 0 && pendingUpdatedSettings.length === 0 && pendingDeletedSettings.length === 0 && (
        <p className="text-xs text-gray-400 mt-1">
          {translate( 'translation:no_chart_settings_yet' )}
        </p>
      )}
    </div>
  );
};

export default ChartSettingsListSection;
